import type { OperationVariables } from '@apollo/client'
import type { SkipToken } from '@apollo/client/react/compiled'
import { useLazyQuery as useBaseLazyQuery } from '@apollo/client/react/compiled'
import { skipToken } from '@apollo/client/react/compiled'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import useAppLoader from '@hooks/useAppLoader'

export type UseLazyQueryResultTuple<
  TData,
  TVariables extends OperationVariables,
> = useBaseLazyQuery.ResultTuple<TData, TVariables, 'empty' | 'complete'>

type UseLazyQueryOpts<TData, TVariables extends OperationVariables> = Partial<
  useBaseLazyQuery.Options<TData, TVariables> & {
    displayLoader: boolean
  }
>

export function useLazyQuery<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  opts: UseLazyQueryOpts<TData, TVariables> | SkipToken,
): UseLazyQueryResultTuple<TData, TVariables> {
  // `defaultOptions.watchQuery` on ApolloClient does not propagate to
  // `useLazyQuery`, so apply the same defaults that the project relies on
  // (errorPolicy: 'all', returnPartialData: false) here. Defaults override
  // caller opts to match the pre-client-preset behaviour.
  const queryTuple = useBaseLazyQuery(
    document,
    (opts === skipToken
      ? opts
      : {
          ...opts,
          errorPolicy: 'all',
          returnPartialData: false,
        }) as useBaseLazyQuery.Options<TData, TVariables>,
  ) as UseLazyQueryResultTuple<TData, TVariables>

  useAppLoader(opts !== skipToken && opts.displayLoader ? queryTuple[1].loading : false)

  return queryTuple
}

type UseLazyQueryHook<TData, TVariables extends OperationVariables> = (
  opts: UseLazyQueryOpts<TData, TVariables>,
) => UseLazyQueryResultTuple<TData, TVariables>

function makeLazyQuery<TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
): UseLazyQueryHook<TData, TVariables> {
  return (opts: UseLazyQueryOpts<TData, TVariables>) => useLazyQuery<TData, TVariables>(query, opts)
}

export default makeLazyQuery
